import { useEffect, useState } from 'react';

import useSelectPlayerModalViewModel from 'application/components/common/SelectPlayerFlow/SelectPlayerModal/useSelectPlayerModalViewModel';
import { usePointCreate, usePointTimezone, usePointSleepSettings } from '@entities/point';
import { useStreamWithThematic } from '@entities/stream';
import { INTEGRATION_PAGE_PATH } from 'application/consts';
import { TEXTS as POINT_TEXTS } from 'application/pages/PointsPage/renderer/texts';

import { toast } from '@shared/ui';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';
import {
  getLastPaidPeriodicInvoice,
  useBillingInfo,
  useAcceptanceAdvanced,
  useExtendSubscription,
  useSubscriptionCost,
} from '@entities/billing';
import useAddressState from 'application/hooks/useAddressState';
import ValidationUtils from 'application/utils/ValidationUtils';
import AnalyticsService, {
  ParamNameAMP,
  PointEventNameAMP,
  StreamBindFlowEventNameAMP,
  StreamBindFlowParamNameAMP,
} from 'application/services/AnalyticsService';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import {
  startAcceptanceAdvancedSigning,
  openSigningPreloadTab,
  selectAcceptanceAdvancedContext,
  DEFAULT_ACCEPTANCE_ADVANCED_ID,
} from '@entities/acceptanceAdvanced';
import {
  CurrentPointOnboardingType,
  CurrentStreamOnboardingType,
  SelectPlayerFlowType,
} from 'application/components/common';
import { RouterUtils } from 'application/providers/RouterProvider';
import { useAccountCompanyId, useAccountSelectors } from 'application/hooks';
import { useAppDispatch, useAppSelector } from '@shared/store';

import { ApolloError, useApolloClient } from '@apollo/client';

import {
  AcceptanceAdvanceStatus,
  BillingPaymentMethodDocument,
  PaymentKind,
  PaymentScheme,
  StandardizedAddressPureType,
  SubscriptionStatus,
  UserFeatures,
} from '@shared/api/graphql/generated';
import { buildAbsoluteUrl } from '@shared/lib';
import { useUserFeaturesOnboarding } from '@entities/media';
import usePaymentMethod, { PaymentMethodType } from '@entities/paymentMethod/api/usePaymentMethod';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';

import {
  DEFAULT_START_TIME,
  DEFAULT_END_TIME,
} from 'application/pages/PointsPage/renderer/PointsDrawer/PointsDrawerContent/SleepSettings/SleepSettingsEditBlock/consts';
import SleepSettingsUtils from 'application/pages/PointsPage/renderer/PointsDrawer/PointsDrawerContent/SleepSettings/SleepSettingsEditBlock/utils';
import { TIME_PATTERN } from '@zvuk-b2b/react-uikit/ui/TimeInput';
import { getPointCreateMutationInput, sendEventsAfterPointCreating } from './utils';
import { PointCreateModalPages, UsePointCreateModalViewModel } from './types';

interface Props {
  flowType?: SelectPlayerFlowType;
  isTemplateMode?: boolean;
  targetStreamId?: string;
  collectionId?: string;
  page: PointCreateModalPages;
  setCurrentPoint: (currentPoint: CurrentPointOnboardingType) => void;
  setCurrentPointStream: (stream: CurrentStreamOnboardingType) => void;
  setCurrentPointIsEnabled: (isEnabled: boolean) => void;
  redirectToPointDrawer: (pointId: string) => void;
  replaceModal: <T extends ModalTypes>(
    type: T,
    modalState: Omit<ModalProps[T], 'open' | 'close' | 'openModal' | 'replaceModal'>,
    onModalCloseCallback?: () => void
  ) => void;
  openModal: ((type: ModalTypes, props: {}) => void) &
    (<T extends ModalTypes>(
      type: T,
      modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
      onModalCloseCallback?: () => void
    ) => void);
}

const URI_FOR_RETURN = buildAbsoluteUrl(RouterUtils.generatePath(INTEGRATION_PAGE_PATH, { type: 'sbbol' }));

const usePointCreateViewModel = ({
  flowType,
  isTemplateMode,
  targetStreamId,
  collectionId,
  page,
  setCurrentPoint,
  setCurrentPointStream,
  setCurrentPointIsEnabled,
  redirectToPointDrawer,
  replaceModal,
  openModal,
}: Props): UsePointCreateModalViewModel => {
  const dispatch = useAppDispatch();

  const selectors = useAccountSelectors();

  const companyId = useAccountCompanyId();
  const companyCountry = useAppSelector(selectors.selectCompanyCountry);
  const companyInvoices = useAppSelector(selectors.selectInvoices);
  const acceptanceAdvancedContext = useAppSelector(selectAcceptanceAdvancedContext(DEFAULT_ACCEPTANCE_ADVANCED_ID));

  const { loading: userOnboardingFeaturesLoading } = useUserFeaturesOnboarding({
    features: [UserFeatures.WarningStreamFirstStart],
  });
  const client = useApolloClient();
  const { request: getPaymentMethod } = usePaymentMethod();

  const readPaymentMethod = (companyId: string): PaymentMethodType => {
    const result = client.readQuery({
      query: BillingPaymentMethodDocument,
      variables: { companyId },
    });

    return (result?.paymentMethodPureQuery || {}) as PaymentMethodType;
  };

  const { paymentMethod } = readPaymentMethod(companyId);

  const { request: createPoint, loading: createPointLoading, error: createPointError } = usePointCreate();
  const { request: pointWithTimezoneRequest, loading: getStandardizedPointsLoading } = usePointTimezone();
  const { request: pointSetSleepSettings } = usePointSleepSettings();

  const getStandardizedPoints = async (queryParams: string) => {
    const result = await pointWithTimezoneRequest(queryParams);

    return result.data?.standardizedAddressPureQuery.result as StandardizedAddressPureType[];
  };

  const [{ extendCost: getExtendSubscriptionCost }, subscriptionCostResult] = useSubscriptionCost();

  const { request: extendOneShotSubscription, result: extendSubscriptionResult } = useExtendSubscription();

  const isExtendSubscriptionLoading = extendSubscriptionResult.loading;
  const licenseCostLoading = subscriptionCostResult.loading;

  const {
    isBillingEnabled,
    hasTrialLicenses,
    hasActiveLicenses,
    selectedPaymentKind,
    paymentScheme,
    status,
    renewableLicenses,
    nextBillingDate,
  } = useBillingInfo(companyId);

  const {
    changeStream,
    isChangeStreamLoading,
    copyTemplateStream,
    isCopyStreamTemplateLoading,
    onboardingPassedRequest,
    isUserFeaturesOnboardingPassLoading,
    getSavedStreams,
    isSavedStreamsLoading,
  } = useSelectPlayerModalViewModel({});

  const { readStreamWithThematic, clearStreamWithThematic } = useStreamWithThematic();

  const { acceptanceAdvanced, loading: acceptanceAdvancedLoading } = useAcceptanceAdvanced(companyId);

  const addressState = useAddressState({ location: companyCountry });

  const [fromTime, setFromTime] = useState(DEFAULT_START_TIME);
  const [toTime, setToTime] = useState(DEFAULT_END_TIME);

  const handleFromTimeBlur = () => {
    if (!fromTime.match(TIME_PATTERN)) {
      setFromTime(DEFAULT_START_TIME);
    }
  };

  const handleToTimeBlur = () => {
    if (!toTime.match(TIME_PATTERN)) {
      setToTime(DEFAULT_END_TIME);
    }
  };

  const isButtonLoading =
    getStandardizedPointsLoading ||
    createPointLoading ||
    isExtendSubscriptionLoading ||
    isChangeStreamLoading ||
    isCopyStreamTemplateLoading ||
    isUserFeaturesOnboardingPassLoading ||
    isSavedStreamsLoading ||
    acceptanceAdvancedLoading;

  const isButtonWithCostLoading =
    isButtonLoading || licenseCostLoading || subscriptionCostResult.data.extendCost === undefined;

  const isAcceptanceAdvancedNull =
    selectedPaymentKind === PaymentKind.Sbbol &&
    (acceptanceAdvanced === null ||
      acceptanceAdvanced?.status === null ||
      acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Canceled) &&
    !hasTrialLicenses;

  const isAcceptanceAdvancedPending =
    selectedPaymentKind === PaymentKind.Sbbol &&
    acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Pending &&
    !hasTrialLicenses;

  const needShowAcceptanceAdvancedModal =
    selectedPaymentKind === PaymentKind.Sbbol && isAcceptanceAdvancedNull && !hasTrialLicenses;

  const isLastInvoiceHasZeroAmount = getLastPaidPeriodicInvoice(companyInvoices || [])?.amount === 0;

  const isAcceptanceAdvancedAuthorizedOrActive =
    acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Authorized ||
    acceptanceAdvanced?.status === AcceptanceAdvanceStatus.Active;

  const canPay = selectedPaymentKind === PaymentKind.Sbbol ? isAcceptanceAdvancedAuthorizedOrActive : paymentMethod;

  const canAddPointWithPayment =
    Boolean(isBillingEnabled) && canPay && !isLastInvoiceHasZeroAmount && hasActiveLicenses;

  const initData = async () => {
    if (companyId) {
      await getPaymentMethod(companyId);
    }

    if (isBillingEnabled) {
      await getExtendSubscriptionCost(companyId, 1);
    }
  };

  useEffect(() => {
    initData();
  }, []);

  const changeStreamOnNewPoint = async (pointId: string) => {
    let streamId = targetStreamId;
    const withThematic = readStreamWithThematic();

    if (isTemplateMode && streamId) {
      streamId = await copyTemplateStream(streamId, companyId, withThematic);
    }

    if (streamId) {
      changeStream([
        {
          newStreamId: streamId,
          pointId,
        },
      ]);

      getSavedStreams([companyId]);
      onboardingPassedRequest({ feature: UserFeatures.WarningStreamFirstStart });

      AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_BOUND, {
        [ParamNameAMP.STREAM_ID]: streamId,
        [ParamNameAMP.COLLECTION_ID]: collectionId,
      });

      setCurrentPointStream({ id: streamId });
      clearStreamWithThematic();
    }
  };

  const onCreatePointModalNext = async () => {
    if (page === PointCreateModalPages.STREAM) {
      AnalyticsService.event(StreamBindFlowEventNameAMP.STREAM_RUN_AT_CREATED_POINT_CLICKED, {
        [StreamBindFlowParamNameAMP.FLOW_TYPE]: flowType,
      });
    }

    AnalyticsService.event(PointEventNameAMP.POINT_CREATE_MODAL_SUCCESS_SUBMIT, {
      [StreamBindFlowParamNameAMP.FLOW_TYPE]: flowType,
    });

    replaceModal(ModalTypes.SELECT_PLAYER_MODAL, {});
  };

  const handleSuccessCreatePointWithPayment = async (pointId: string, licenseId?: string) => {
    try {
      if (canAddPointWithPayment && licenseId) {
        await extendOneShotSubscription(companyId, [licenseId]);

        toast.info(POINT_TEXTS.POINT_CREATE_CONFIRM_MODAL_WITH_PAYMENT_SUCCESS_TEXT);

        setCurrentPointIsEnabled(true);
      }
    } catch (e) {
      toast.error(DEFAULT_ERROR_MESSAGE);
    } finally {
      if (page === PointCreateModalPages.STREAM) {
        await changeStreamOnNewPoint(pointId);
      } else {
        redirectToPointDrawer(pointId);
      }

      onCreatePointModalNext();
    }
  };

  const handleCreateClick = async () => {
    const input = await getPointCreateMutationInput(companyId, addressState.value, getStandardizedPoints);

    let result;
    try {
      result = await createPoint(input);
    } catch (error) {
      if (!(error instanceof ApolloError)) throw error;
      return;
    }

    if (result.ok && result.point) {
      const sleepSettings = SleepSettingsUtils.getSleepSettings(fromTime, toTime);
      await pointSetSleepSettings({
        pointId: result.point.id,
        sleepMode: true,
        sleepSettings,
      });

      sendEventsAfterPointCreating(result.point.id, result.point.isEnabled, page);
      setCurrentPoint(result.point);
      await handleSuccessCreatePointWithPayment(result.point.id, result.point.license?.id);
    } else {
      toast.error(DEFAULT_ERROR_MESSAGE);
    }
  };

  const onPointCreateConfirmModalShow = () => {
    openModal(ModalTypes.POINT_CREATE_CONFIRM, {
      onSubmit: async () => {
        await handleCreateClick();
      },
      cost: subscriptionCostResult.data.extendCost?.cost,
    });
  };

  const openAcceptanceAdvancedSigning = (preOpenedTab?: Window | null) => {
    const hasActiveTab = acceptanceAdvancedContext?.hasActiveTab;
    let tab: Window | null;

    if (preOpenedTab !== undefined) {
      tab = preOpenedTab;
    } else if (!hasActiveTab) {
      tab = openSigningPreloadTab();
    } else {
      tab = null;
    }

    dispatch(
      startAcceptanceAdvancedSigning({
        companyId,
        uriForReturn: URI_FOR_RETURN,
        fallbackFn: () => {
          openModal(ModalTypes.SBERBUSSINESS_OPEN, {});
        },
        preOpenedTab: tab,
      })
    );
  };

  const startDate = new Date();
  const endDate = nextBillingDate
    ? DateUtils.subDays(new Date(nextBillingDate), 1)
    : DateUtils.getLastDayOfMonth(startDate);

  const formatStartNextBillingDate = DateUtils.getFormatDateFrom(startDate, 'dd.MM');
  const formatEndNextBillingDate = DateUtils.getFormatDateFrom(endDate, 'dd.MM');

  return {
    addressState,
    extendCost: subscriptionCostResult.data.extendCost?.cost,
    extendFullPeriodCost:
      (subscriptionCostResult.data.extendCost?.fullPeriodCost || 0) * (renewableLicenses?.length || 1),
    createPointError,
    userOnboardingFeaturesLoading,
    isButtonDisabled:
      !ValidationUtils.validateAddress(addressState.value) || isAcceptanceAdvancedPending || isAcceptanceAdvancedNull,
    isButtonLoading: canAddPointWithPayment ? isButtonWithCostLoading : isButtonLoading,
    isShowHintAboutPrice: Boolean(isBillingEnabled) && !hasTrialLicenses,
    needShowAcceptanceAdvancedModal,
    isAcceptanceAdvancedNull:
      isAcceptanceAdvancedNull || (selectedPaymentKind === PaymentKind.Sbbol && acceptanceAdvancedLoading),
    isAcceptanceAdvancedPending,
    canAddPointWithPayment,
    withResidualPayment:
      paymentScheme === PaymentScheme.InvoiceCalendarMonthly && status !== SubscriptionStatus.InactiveSubscription,
    formatNextBillingDate: `${formatStartNextBillingDate} - ${formatEndNextBillingDate}`,
    fromTime,
    toTime,
    onFromTimeChange: setFromTime,
    onToTimeChange: setToTime,
    onFromTimeBlur: handleFromTimeBlur,
    onToTimeBlur: handleToTimeBlur,
    onCreatePointButtonClick: canAddPointWithPayment ? onPointCreateConfirmModalShow : handleCreateClick,
    openAcceptanceAdvancedSigning,
  };
};

export default usePointCreateViewModel;
